import type { PublicDayAvailability } from '../../core/models/public-day-availability';

/** Six rows, as tall as a month grid gets: thirty days fit on one page even from a Sunday. */
export const WEEKS_PER_PAGE = 6;

/** One day cell of the calendar. */
export interface WeekCell {
    /** `"YYYY-MM-DD"`, the same string the slots route returned. */
    readonly date: string;
    readonly dayOfMonth: number;
    /** The day is open and has times on it, so clicking it means something. */
    readonly selectable: boolean;
    /** Draws the dot under the number. */
    readonly hasSlots: boolean;
    /** The 1st of a month, which is labelled so a page crossing a month says where it does. */
    readonly startsMonth: boolean;
}

/**
 * One page of the booking calendar: whole weeks, Monday first, starting from the week that holds
 * the window's first day — the server's today — so today is always on the top row and the page
 * never opens on a month of days that have already gone.
 *
 * **Nothing here reads the device's clock or its zone.** The window's first day is the server's
 * today, and a day is offered if, and only if, the availability window contains it and says it is
 * open. Date arithmetic is on the date's own three numbers through `Date.UTC`, never a parse of the
 * string, which would be read as UTC midnight and rendered a day earlier west of Greenwich.
 */
export function buildWeekPage(
    windowStart: string,
    page: number,
    days: ReadonlyMap<string, PublicDayAvailability>
): WeekCell[] {
    const first = addDays(mondayOf(windowStart), page * WEEKS_PER_PAGE * 7);

    return Array.from({ length: WEEKS_PER_PAGE * 7 }, (_, index) => {
        const date = addDays(first, index);
        const day = days.get(date);
        const open = day?.availability === 'Open' && day.slots.length > 0;
        const dayOfMonth = Number(date.slice(8, 10));

        return { date, dayOfMonth, selectable: open, hasSlots: open, startsMonth: dayOfMonth === 1 };
    });
}

/** How many pages it takes to reach the window's last day. */
export function pageCount(windowStart: string, windowEnd: string): number {
    return pageOf(windowStart, windowEnd) + 1;
}

/** The page a date falls on, counted from the page that opens on the window's first week. */
export function pageOf(windowStart: string, date: string): number {
    const days = (utc(date) - utc(mondayOf(windowStart))) / DAY;

    return Math.max(0, Math.floor(days / (WEEKS_PER_PAGE * 7)));
}

/** The Monday on or before a date. */
export function mondayOf(date: string): string {
    const weekday = (new Date(utc(date)).getUTCDay() + 6) % 7;

    return addDays(date, -weekday);
}

export function addDays(date: string, by: number): string {
    const shifted = new Date(utc(date) + by * DAY);

    return [
        String(shifted.getUTCFullYear()).padStart(4, '0'),
        String(shifted.getUTCMonth() + 1).padStart(2, '0'),
        String(shifted.getUTCDate()).padStart(2, '0')
    ].join('-');
}

const DAY = 24 * 60 * 60 * 1000;

function utc(date: string): number {
    const [year, month, day] = date.split('-').map(Number);

    return Date.UTC(year, month - 1, day);
}
